import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { QueryState } from "@/shared/ui/QueryState";
import { Badge, Button, Card, SimplePagination } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { ChevronRight, ClipboardCheck, Eye } from "lucide-react";
import { useEffect } from "react";
import {
  useInspectorStandardsQuery,
  useMyQcRecordsQuery,
  useQcInspectorStatusQuery,
} from "../api/queries";

function statusBadge(status: "pass" | "fail" | "pending") {
  if (status === "pass")
    return (
      <Badge variant="secondary" className="bg-emerald-600/15 text-emerald-800">
        ຜ່ານ
      </Badge>
    );
  if (status === "fail") return <Badge variant="destructive">ບໍ່ຜ່ານ</Badge>;
  return <Badge variant="outline">ລໍຖ້າ</Badge>;
}

const MY_RECORDS_PAGE_SIZE = 10;

export function QcInspectorHubPage() {
  const statusQuery = useQcInspectorStatusQuery({ enabled: true });
  const standardsQuery = useInspectorStandardsQuery({
    enabled: Boolean(statusQuery.data?.isInspector),
  });
  const nav = useNavigate({ from: "/app/qc/inspection" });
  const search = useSearch({ from: "/app/qc/inspection" });
  const page = search.page;
  const offset = (page - 1) * MY_RECORDS_PAGE_SIZE;

  const recordsQuery = useMyQcRecordsQuery({
    enabled: Boolean(statusQuery.data?.isInspector),
    limit: MY_RECORDS_PAGE_SIZE,
    offset,
  });

  const total = recordsQuery.data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / MY_RECORDS_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const rows = recordsQuery.data?.data ?? [];

  useEffect(() => {
    if (!recordsQuery.isSuccess) return;
    if (total === 0 && page > 1) {
      nav({ search: { ...search, page: 1 }, replace: true });
      return;
    }
    if (total > 0 && page > totalPages) {
      nav({ search: { ...search, page: totalPages }, replace: true });
    }
  }, [recordsQuery.isSuccess, page, totalPages, total, nav, search]);

  if (statusQuery.isLoading) {
    return <QueryState isLoading>ກຳລັງໂຫຼດ...</QueryState>;
  }

  if (statusQuery.isError) {
    return (
      <QueryState isError error={statusQuery.error}>
        ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນຜູ້ກວດສອບ QC
      </QueryState>
    );
  }

  if (!statusQuery.data?.isInspector) {
    return (
      <>
        <Header />
        <Main className="flex flex-col gap-4 p-6">
          <Card className="border-border p-6">
            <p className="text-muted-foreground">
              ບັນຊີຂອງທ່ານບໍ່ໄດ້ຖືກກຳນົດເປັນຜູ້ກວດສອບຕົວບຸກຄົນໃນມາດຕະຖານ QC ທີ່ໃຊ້ງານ.
            </p>
          </Card>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6 p-4 md:p-6">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-8 w-8 text-primary" />
          <h1 className="font-semibold text-2xl">ກວດສອບ QC</h1>
        </div>

        <section className="space-y-3">
          <h2 className="font-medium text-lg">ເລືອກມາດຕະຖານເພື່ອບັນທຶກການກວດ</h2>
          {standardsQuery.isLoading ? (
            <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດລາຍການ...</p>
          ) : standardsQuery.isError ? (
            <p className="text-destructive text-sm">ໂຫຼດມາດຕະຖານບໍ່ສຳເລັດ</p>
          ) : !standardsQuery.data?.length ? (
            <Card className="border-border p-4">
              <p className="text-muted-foreground text-sm">
                ຍັງບໍ່ມີມາດຕະຖານ QC ທີ່ໃຊ້ງານສຳລັບທ່ານ.
              </p>
            </Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-3">
              {standardsQuery.data.map((s) => (
                <Card key={s.id} className="border-border p-4">
                  <div className="flex flex-col gap-2">
                    <div className="font-medium">{s.name}</div>
                    <div className="text-muted-foreground text-xs">
                      {s.workTypeName}
                      {s.workStage ? ` · ${s.workStage}` : ""} · {s.itemCount}{" "}
                      ລາຍການກວດ
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 w-fit"
                      onClick={() => {
                        nav({
                          to: "/app/qc/inspection/$standardId",
                          params: { standardId: s.id },
                        });
                      }}
                    >
                      ເລີ່ມບັນທຶກການກວດ
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <h2 className="font-medium text-lg">ບັນທຶກຫຼ້າສຸດຂອງຂ້ອຍ</h2>
          {recordsQuery.isLoading ? (
            <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
          ) : recordsQuery.isError ? (
            <p className="text-destructive text-sm">ໂຫຼດບັນທຶກບໍ່ສຳເລັດ</p>
          ) : !rows.length ? (
            <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີບັນທຶກ</p>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">ວັນທີ</th>
                      <th className="px-3 py-2 text-left font-medium">
                        ມາດຕະຖານ
                      </th>
                      <th className="px-3 py-2 text-left font-medium">ຜົນ</th>
                      <th className="px-3 py-2 text-left font-medium">ງານ</th>
                      <th className="px-3 py-2 text-right font-medium">
                        ລາຍລະອຽດ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-border/60 border-b">
                        <td className="whitespace-nowrap px-3 py-2">
                          {format(new Date(r.inspectedAt), "yyyy-MM-dd HH:mm")}
                        </td>
                        <td className="px-3 py-2">{r.standardName}</td>
                        <td className="px-3 py-2">
                          {statusBadge(r.overallStatus)}
                        </td>
                        <td className="max-w-[12rem] truncate px-3 py-2 text-muted-foreground">
                          {r.workItemId ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() => {
                              nav({
                                to: "/app/qc/inspection/record/$recordId",
                                params: { recordId: r.id },
                              });
                            }}
                          >
                            <Eye className="size-3.5" aria-hidden />
                            ເບິ່ງ
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 ? (
                <div className="flex flex-col items-center gap-2">
                  <SimplePagination
                    page={safePage}
                    totalPages={totalPages}
                    onPageChange={(p) =>
                      nav({ search: { ...search, page: p } })
                    }
                  />
                  <p className="text-center text-muted-foreground text-xs">
                    ໜ້າ {safePage} ຈາກ {totalPages} · ທັງໝົດ {total} ບັນທຶກ
                  </p>
                </div>
              ) : (
                <p className="text-center text-muted-foreground text-xs">
                  {total} ບັນທຶກ
                </p>
              )}
            </div>
          )}
        </section>
      </Main>
    </>
  );
}
